import { apiClient } from '@shared/utils/api-client';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getWebPushPermission(): Promise<NotificationPermission> {
  if (!isWebPushSupported()) return 'denied';
  return Notification.permission;
}

async function getPushRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isWebPushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  // В проде SW регистрируется сразу после старта. В dev его нет — ready тогда не резолвится.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), 4000);
    }),
  ]);
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  const reg = await getPushRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Браузерная подписка одна на устройство, а на сервере она привязана к магазину.
 * Если пуши уже разрешены, заново отправляем её текущему кабинету — иначе
 * сообщения уходят магазину, под которым устройство включали в прошлый раз.
 */
export async function syncSellerWebPushWithServer(): Promise<void> {
  if (!isWebPushSupported() || Notification.permission !== 'granted') return;
  const reg = await getPushRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
  await apiClient.post('/seller-notifications/me/web-push', {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  });
}

export async function enableSellerWebPush(): Promise<void> {
  if (!isWebPushSupported()) {
    throw new Error('Web Push не поддерживается в этом браузере');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    if (permission === 'denied') {
      throw new Error(
        'Уведомления заблокированы для этого сайта. В Chrome: ⋮ → Настройки сайта → Уведомления → Разрешить, затем снова нажмите Вкл',
      );
    }
    throw new Error('Разрешение на уведомления не выдано');
  }

  const { publicKey } = await apiClient.get<{ publicKey: string }>(
    '/seller-notifications/me/web-push/vapid-public-key',
  );

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Не удалось получить ключи подписки');
  }

  await apiClient.post('/seller-notifications/me/web-push', {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  });

  // Android создаёт канал уведомлений («Общие») только после первого показа.
  // Шлём тестовый сразу, чтобы продавец мог включить все тумблеры до реального заказа.
  try {
    await reg.showNotification('Пуши включены', {
      body: 'Так будут приходить заказы, сообщения и уведомления. Если звука не было — в настройках уведомлений Chrome откройте новую категорию (часто «Общие») и включите все тумблеры.',
      icon: '/pwa/icon-192.png',
      badge: '/pwa/icon-192.png',
      tag: 'seller-web-push-test',
      renotify: true,
      requireInteraction: true,
      data: { url: '/' },
    } as NotificationOptions);
  } catch {
    // Подписка уже сохранена — тестовый пуш не критичен
  }
}

export async function disableSellerWebPush(): Promise<void> {
  if (!isWebPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => undefined);
  await apiClient
    .delete('/seller-notifications/me/web-push', { endpoint })
    .catch(() => undefined);
}
